import { AccrualImporterRow } from "../types";
import { withDerivedAccrualLineAmount } from "../accrualImporterLineAmount.helpers";

function isStringArray(value: unknown): value is string[] {
    return (
        Array.isArray(value) &&
        value.every((entry) => typeof entry === "string")
    );
}

export function isAccrualImporterRowKey(
    field: string,
): field is keyof AccrualImporterRow {
    switch (field) {
        case "createdDate":
        case "counterpartyId":
        case "counterpartyName":
        case "invoiceNumber":
        case "memo":
        case "productOrService":
        case "description":
        case "classIds":
        case "quantity":
        case "unitPrice":
        case "amount":
        case "dueDate":
        case "accrualLedger":
        case "accrualAccountId":
        case "entityId":
        case "financialAccountId":
        case "totalAmount":
        case "extractionWarnings":
        case "status":
            return true;
        default:
            return false;
    }
}

export function applySpreadsheetColumnUpdate({
    row,
    columnField,
    incoming,
}: {
    row: AccrualImporterRow;
    columnField: string;
    incoming: Record<string, unknown>;
}): AccrualImporterRow {
    if (!isAccrualImporterRowKey(columnField)) {
        return row;
    }

    if (!Object.hasOwn(incoming, columnField)) {
        return row;
    }

    const value = incoming[columnField];

    if (value === undefined) {
        switch (columnField) {
            case "description":
            case "invoiceNumber":
            case "memo":
            case "createdDate":
            case "dueDate":
            case "accrualLedger":
            case "accrualAccountId":
            case "status":
                return { ...row, [columnField]: "" };
            case "productOrService":
                return {
                    ...row,
                    productOrService: "",
                    catalogItemId: null,
                    catalogItemPriceId: null,
                };
            case "counterpartyId":
            case "counterpartyName":
            case "classIds":
            case "quantity":
            case "unitPrice":
            case "totalAmount":
            case "entityId":
            case "financialAccountId":
                return { ...row, [columnField]: null };
            case "amount":
                return withDerivedAccrualLineAmount(row);
            case "extractionWarnings":
                return { ...row, extractionWarnings: [] };
            default:
                return row;
        }
    }

    switch (columnField) {
        case "description":
        case "invoiceNumber":
        case "memo":
        case "createdDate":
        case "dueDate":
        case "accrualLedger":
        case "accrualAccountId":
        case "status":
            return typeof value === "string"
                ? { ...row, [columnField]: value }
                : row;
        case "productOrService":
            return typeof value === "string"
                ? {
                      ...row,
                      productOrService: value,
                      catalogItemId: null,
                      catalogItemPriceId: null,
                  }
                : row;
        case "counterpartyId":
        case "counterpartyName":
            return value === null || typeof value === "string"
                ? { ...row, [columnField]: value }
                : row;
        case "classIds":
            return value === null || isStringArray(value)
                ? { ...row, classIds: value }
                : row;
        case "quantity":
        case "unitPrice":
        case "totalAmount":
        case "entityId":
        case "financialAccountId":
            return value === null || typeof value === "number"
                ? { ...row, [columnField]: value }
                : row;
        case "amount":
            return withDerivedAccrualLineAmount(row);
        case "extractionWarnings":
            return isStringArray(value)
                ? { ...row, extractionWarnings: value }
                : row;
        default:
            return row;
    }
}
